/**
 * Laning Heuristics - Static Human Knowledge Baseline
 *
 * Encodes expert knowledge about piece placement (laning) as numerical bonuses.
 * This provides an immediate baseline for column assignment and piece selection,
 * which learned data gradually adjusts over time.
 *
 * Four tables:
 * A) Same-lane opponent matchups (our piece vs opponent in same column)
 * B) Adjacent-lane opponent matchups (±1 column influence)
 * C) Ally adjacency bonuses (placing allies next to each other)
 * D) Column preferences (inherent column affinity)
 */

import { PieceType } from '../../zodiacEngine/types';

// ─── A) Same-Lane Opponent Matchups ─────────────────────────────
// Positive = good for our piece, Negative = bad for our piece
// Scale: -20 to +20

const SAME_LANE_VS_OPPONENT: Partial<Record<PieceType, Partial<Record<PieceType, number>>>> = {
  [PieceType.Bear]: {
    [PieceType.Rhino]: 15,    // Bear captures through Rhino's defense
    [PieceType.Monkey]: 12,   // Bear overpowers Monkey (66% fail for Monkey)
    [PieceType.Goat]: 10,     // Bear captures Goat (Goat can't capture predators)
  },
  [PieceType.Crane]: {
    [PieceType.Monkey]: 12,   // Crane range picks off Monkey
    [PieceType.Fungi]: 12,    // Crane range clears Fungi
    [PieceType.Goat]: 10,     // Crane outranges Goat
    [PieceType.Snake]: 10,    // Crane outranges Snake
  },
  [PieceType.Frog]: {
    [PieceType.Wolf]: 8,      // Frog defends well vs Wolf's approach
    [PieceType.Crane]: 8,     // Diagonal movement dodges Crane's straight lines
    [PieceType.Goat]: 8,      // Frog poisons Goat on capture
    [PieceType.Bear]: 5,      // Mild advantage with poison threat
    [PieceType.Snake]: -15,   // Snake outranges and kills Frog in lane
    [PieceType.Rhino]: -15,   // Rhino charges straight through Frog
    [PieceType.Fungi]: -8,    // Fungi meadow disrupts Frog's allies
    [PieceType.Tree]: -8,     // Tree blocks Frog's diagonal paths
  },
  [PieceType.Fungi]: {
    [PieceType.Tree]: 15,     // Meadow completely shuts down Tree
    [PieceType.Octopus]: 12,  // Fungi counters Octopus (non-animal)
    [PieceType.Crane]: -12,   // Crane range picks off Fungi
    [PieceType.Monkey]: -15,  // Monkey consumes Fungi
  },
  [PieceType.Goat]: {
    [PieceType.Bear]: -10,    // Goat can't capture Bear (predator)
    [PieceType.Crane]: -10,   // Crane outranges Goat
  },
  [PieceType.Monkey]: {
    // Note: Monkey's laning is context-dependent (much stronger WITH allied Tree)
    // Tree-dependent bonuses are handled in getExpectedLaningScoreWithContext()
    [PieceType.Bear]: -12,    // Bear overpowers Monkey
    [PieceType.Crane]: -8,    // Crane outranges Monkey
  },
  [PieceType.Octopus]: {
    [PieceType.Bear]: 12,     // Entangle stops Bear's approach
    [PieceType.Wolf]: 12,     // Entangle stops Wolf's movement
    [PieceType.Crane]: 10,    // Entangle disrupts Crane
    [PieceType.Tree]: -15,    // Tree houses pieces, blocks Octopus
    [PieceType.Fungi]: -15,   // Fungi meadow zones, non-animal
  },
  [PieceType.Penguin]: {
    [PieceType.Wolf]: 8,      // Wolf leaves lane, Penguin slides through
    [PieceType.Goat]: 8,      // Goat leaves lane via traverse
    [PieceType.Rhino]: -10,   // Rhino stays in lane, blocks slides
    [PieceType.Snake]: -10,   // Snake controls lane, blocks Penguin
    [PieceType.Tree]: -12,    // Tree blocks slides, Penguin can't climb
  },
  [PieceType.Rhino]: {
    [PieceType.Frog]: 15,     // Rhino charges through Frog
    [PieceType.Snake]: 12,    // Rhino overpowers Snake in lane
    [PieceType.Wolf]: 5,      // Wolf in same lane can't L-move to block charge — Rhino benefits
    [PieceType.Bear]: -15,    // Bear's savage overrides Rhino's defense
    [PieceType.Octopus]: -10, // Octopus entangles Rhino
  },
  [PieceType.Snake]: {
    [PieceType.Frog]: 10,     // Snake outranges Frog in same lane
    [PieceType.Crane]: -12,   // Crane outranges Snake
    [PieceType.Rhino]: -12,   // Rhino charges through Snake
    [PieceType.Wolf]: -8,     // Wolf's mobility outmaneuvers Snake
  },
  [PieceType.Tree]: {
    [PieceType.Octopus]: 15,  // Tree houses pieces, blocks Octopus
    [PieceType.Fungi]: -15,   // Fungi meadow immobilizes Tree
    [PieceType.Rhino]: -10,   // Rhino crushes through Tree
  },
  [PieceType.Wolf]: {
    [PieceType.Rhino]: -5,    // Wolf in same lane as Rhino is bad — can't L-move to block charge
    [PieceType.Snake]: 8,     // Wolf outmaneuvers Snake
    [PieceType.Bear]: -15,    // Bear savage kills Wolf
    [PieceType.Octopus]: -10, // Octopus entangles Wolf
  },
  [PieceType.Virus]: {
    // Virus is good vs animals, terrible vs non-animals
    [PieceType.Bear]: 3,
    [PieceType.Crane]: 3,
    [PieceType.Frog]: 3,
    [PieceType.Goat]: 3,
    [PieceType.Monkey]: 3,
    [PieceType.Octopus]: 3,
    [PieceType.Penguin]: 3,
    [PieceType.Rhino]: 3,
    [PieceType.Snake]: 3,
    [PieceType.Wolf]: 3,
    [PieceType.Tree]: -20,    // Can't infect Tree
    [PieceType.Fungi]: -20,   // Can't infect Fungi
  },
};

// ─── B) Adjacent-Lane Opponent Matchups ─────────────────────────
// Pieces that project influence ±1 column. ~60% of same-lane values.

const ADJACENT_LANE_VS_OPPONENT: Partial<Record<PieceType, Partial<Record<PieceType, number>>>> = {
  [PieceType.Bear]: {
    [PieceType.Rhino]: 10,
    [PieceType.Monkey]: 8,
    [PieceType.Goat]: 6,
  },
  [PieceType.Crane]: {
    [PieceType.Monkey]: 8,
    [PieceType.Fungi]: 8,
    [PieceType.Goat]: 6,
    [PieceType.Snake]: 6,
  },
  [PieceType.Octopus]: {
    [PieceType.Bear]: 8,     // Diagonal entangle reaches adjacent lanes
    [PieceType.Wolf]: 8,
    [PieceType.Crane]: 6,
  },
  [PieceType.Rhino]: {
    [PieceType.Wolf]: -12,   // Wolf in adjacent lane can L-move to block Rhino's charge
  },
  [PieceType.Wolf]: {
    // Wolf counters Rhino from adjacent lane — L-move blocks/captures charging Rhino
    [PieceType.Rhino]: 15,
    [PieceType.Snake]: 5,
    [PieceType.Frog]: 4,
    [PieceType.Goat]: 3,
  },
};

// ─── C) Ally Adjacency Bonuses ──────────────────────────────────
// Bonus for having a specific ally in ±1 column

const ALLY_ADJACENCY: Partial<Record<PieceType, Partial<Record<PieceType, number>>>> = {
  [PieceType.Monkey]: {
    [PieceType.Tree]: 20,     // Monkey swings from Tree — essential combo
    [PieceType.Fungi]: 8,     // Monkey consumes Fungi for ability
  },
  [PieceType.Tree]: {
    [PieceType.Monkey]: 20,   // Tree houses Monkey — essential combo
  },
  [PieceType.Wolf]: {
    [PieceType.Rhino]: 12,    // Wolf covers Rhino's flanks
  },
  [PieceType.Rhino]: {
    [PieceType.Wolf]: 12,     // Rhino benefits from Wolf's lane control
  },
  [PieceType.Fungi]: {
    [PieceType.Monkey]: 8,    // Fungi near Monkey for consume synergy
  },
};

// ─── D) Column Preferences ─────────────────────────────────────
// Inherent affinity for specific columns (0-4)

const COLUMN_PREFERENCE: Partial<Record<PieceType, Record<number, number>>> = {
  [PieceType.Monkey]: {
    0: 0, 1: 5, 2: 5, 3: 5, 4: 0,  // Middle columns for board control
  },
  [PieceType.Penguin]: {
    0: 3, 1: 0, 2: 0, 3: 0, 4: 3,  // Edge columns, fewer blockers
  },
};

// ─── Exported Functions ─────────────────────────────────────────

/**
 * Get the same-lane matchup score for our piece vs an opponent piece.
 * Positive = favorable for us.
 */
export function getLaningSameCol(ourPiece: PieceType, oppPiece: PieceType): number {
  return SAME_LANE_VS_OPPONENT[ourPiece]?.[oppPiece] ?? 0;
}

/**
 * Get the adjacent-lane matchup score for our piece vs an opponent piece.
 * Positive = favorable for us. Only populated for cross-lane influence pieces.
 */
export function getLaningAdjacentCol(ourPiece: PieceType, oppPiece: PieceType): number {
  return ADJACENT_LANE_VS_OPPONENT[ourPiece]?.[oppPiece] ?? 0;
}

/**
 * Get the bonus for placing our piece adjacent to an ally.
 * Positive = beneficial adjacency.
 */
export function getAllyAdjacencyBonus(piece: PieceType, ally: PieceType): number {
  return ALLY_ADJACENCY[piece]?.[ally] ?? 0;
}

/**
 * Get the column preference for a piece at a specific column.
 */
export function getColumnPreference(piece: PieceType, col: number): number {
  return COLUMN_PREFERENCE[piece]?.[col] ?? 0;
}

/**
 * Check if a piece has any ally adjacency preferences.
 * Used to prioritize column assignment order.
 */
export function hasAllyAdjacencyPrefs(piece: PieceType): boolean {
  const prefs = ALLY_ADJACENCY[piece];
  return prefs !== undefined && Object.keys(prefs).length > 0;
}

// ─── All piece types for averaging ──────────────────────────────
const ALL_PIECE_TYPES: PieceType[] = [
  PieceType.Bear, PieceType.Crane, PieceType.Frog, PieceType.Fungi,
  PieceType.Goat, PieceType.Monkey, PieceType.Octopus, PieceType.Penguin,
  PieceType.Rhino, PieceType.Snake, PieceType.Tree, PieceType.Wolf,
  PieceType.Virus,
];

/**
 * Get a general laning quality score for a piece across all possible opponents.
 * Used during piece SELECTION when opponent army is unknown.
 *
 * For each potential opponent piece, assumes:
 * - 1/5 chance of same-column matchup
 * - 2/5 chance of adjacent-column matchup
 * Then averages across all 13 piece types.
 *
 * Returns a value roughly in range [-10, +10] for typical pieces.
 */
export function getGeneralLaningQuality(piece: PieceType): number {
  let totalScore = 0;

  for (const opp of ALL_PIECE_TYPES) {
    if (opp === piece) continue; // Can't face yourself
    const sameCol = getLaningSameCol(piece, opp);
    const adjCol = getLaningAdjacentCol(piece, opp);
    // Weight: 1/5 same column, 2/5 adjacent columns (left + right)
    totalScore += (sameCol * 0.2) + (adjCol * 0.4);
  }

  // Average across 12 opponents (exclude self)
  return totalScore / 12;
}
